import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
  Avatar,
  Tooltip,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Visibility as VisibilityIcon,
  Folder as FolderIcon,
  EventAvailable as EventAvailableIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { subClientService, subClientGroupService, paysService } from '../services/api';
import { SubClient, SubClientGroup, Pays } from '../types';
import { useNavigate, useLocation } from 'react-router-dom';
import IDScannerModal from '../components/FieldAgent/IDScannerModal';

function normalizePhoneDigits(s: string): string {
  return s.replace(/\D/g, '');
}

function subClientDisplayName(c: SubClient): string {
  if (c.type === 'company' && c.company_name) return c.company_name;
  return c.name || [c.prenom, c.nom].filter(Boolean).join(' ') || '—';
}

interface SubClientFormData {
  name: string;
  nom: string;
  prenom: string;
  email: string;
  phone: string;
  type: 'individual' | 'company';
  company_name: string;
  siret: string;
  address: string;
  city: string;
  postal_code: string;
  country: string;
  notes: string;
  is_active: boolean;
  sub_client_group_id: number | '';
}

const SubClients: React.FC = () => {
  const [subClients, setSubClients] = useState<SubClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSubClient, setEditingSubClient] = useState<SubClient | null>(null);
  const [formData, setFormData] = useState<SubClientFormData>({
    name: '',
    nom: '',
    prenom: '',
    email: '',
    phone: '',
    type: 'individual',
    company_name: '',
    siret: '',
    address: '',
    city: '',
    postal_code: '',
    country: '',
    notes: '',
    is_active: true,
    sub_client_group_id: '',
  });
  const navigate = useNavigate();
  const location = useLocation();
  const [highlightedId, setHighlightedId] = useState<number | null>(null);

  // Groups state
  const [groups, setGroups] = useState<SubClientGroup[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupFilter, setGroupFilter] = useState<number | ''>('');
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<SubClientGroup | null>(null);
  const [groupForm, setGroupForm] = useState({ name: '', description: '' });
  const [selectedSubClientIds, setSelectedSubClientIds] = useState<number[]>([]);
  const [allSubClientsForGroup, setAllSubClientsForGroup] = useState<SubClient[]>([]);
  const [loadingGroupSubClients, setLoadingGroupSubClients] = useState(false);
  const [savingGroup, setSavingGroup] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [paysList, setPaysList] = useState<Pays[]>([]);
  const [duplicateEmailClient, setDuplicateEmailClient] = useState<SubClient | null>(null);
  const [duplicatePhoneClient, setDuplicatePhoneClient] = useState<SubClient | null>(null);

  // ID scanner state
  const [idScannerOpen, setIdScannerOpen] = useState(false);
  const [idScannerTarget, setIdScannerTarget] = useState<SubClient | null>(null);

  useEffect(() => {
    loadGroups();
    paysService.getAll().then((r) => setPaysList(r.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadSubClients();
  }, [groupFilter]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const focusedId = params.get('subClientId');
    if (focusedId) {
      const numericId = parseInt(focusedId, 10);
      if (!Number.isNaN(numericId)) {
        setHighlightedId(numericId);
        // Scroll into view after table renders
        setTimeout(() => {
          const target = document.getElementById(`sub-client-row-${numericId}`);
          if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      }
    } else {
      setHighlightedId(null);
    }
  }, [location.search, subClients.length]);

  const loadSubClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = groupFilter ? { sub_client_group_id: groupFilter } : undefined;
      const response = await subClientService.getAll(params);
      setSubClients(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load sub-clients');
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      setLoadingGroups(true);
      const response = await subClientGroupService.getAll();
      setGroups(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load groups');
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleSubmit = async () => {
    try {
      // Construct full name from nom and prenom
      const fullName = `${formData.nom || ''} ${formData.prenom || ''}`.trim();
      const submitData: Record<string, unknown> = {
        ...formData,
        name: fullName,
      };
      if (formData.sub_client_group_id === '') {
        submitData.sub_client_group_id = null;
      } else {
        submitData.sub_client_group_id = formData.sub_client_group_id;
      }

      if (editingSubClient) {
        await subClientService.update(editingSubClient.id, submitData);
      } else {
        await subClientService.create(submitData);
      }
      setOpenDialog(false);
      setEditingSubClient(null);
      resetForm();
      loadSubClients();
      loadGroups();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save sub-client');
    }
  };

  const handleEdit = (subClient: SubClient) => {
    setEditingSubClient(subClient);
    setFormData({
      name: subClient.name,
      nom: subClient.nom || '',
      prenom: subClient.prenom || '',
      email: subClient.email,
      phone: subClient.phone || '',
      type: subClient.type,
      company_name: subClient.company_name || '',
      siret: subClient.siret || '',
      address: subClient.address || '',
      city: subClient.city || '',
      postal_code: subClient.postal_code || '',
      country: subClient.country || '',
      notes: subClient.notes || '',
      is_active: subClient.is_active,
      sub_client_group_id: subClient.sub_client_group_id ?? '',
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this sub-client?')) {
      try {
        await subClientService.delete(id);
        loadSubClients();
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to delete sub-client');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      nom: '',
      prenom: '',
      email: '',
      phone: '',
      type: 'individual',
      company_name: '',
      siret: '',
      address: '',
      city: '',
      postal_code: '',
      country: '',
      notes: '',
      is_active: true,
      sub_client_group_id: '',
    });
  };

  const handleSaveGroup = async () => {
    try {
      setSavingGroup(true);
      setError(null);
      const payload = { ...groupForm, sub_client_ids: selectedSubClientIds };
      if (editingGroup) {
        await subClientGroupService.update(editingGroup.id, payload);
      } else {
        await subClientGroupService.create(payload);
      }
      setGroupDialogOpen(false);
      setEditingGroup(null);
      setGroupForm({ name: '', description: '' });
      setSelectedSubClientIds([]);
      loadGroups();
      loadSubClients();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save group');
    } finally {
      setSavingGroup(false);
    }
  };

  const handleDeleteGroup = async (group: SubClientGroup) => {
    if (!window.confirm(`Supprimer le groupe « ${group.name} » ? Les clients du groupe ne seront pas supprimés.`)) return;
    try {
      await subClientGroupService.delete(group.id);
      if (groupFilter === group.id) setGroupFilter('');
      loadGroups();
      loadSubClients();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete group');
    }
  };

  const openEditGroup = async (group: SubClientGroup) => {
    setEditingGroup(group);
    setGroupForm({ name: group.name, description: group.description || '' });
    setGroupDialogOpen(true);
    setLoadingGroupSubClients(true);
    try {
      const [groupRes, subClientsRes] = await Promise.all([
        subClientGroupService.getById(group.id),
        subClientService.getAll(),
      ]);
      const g = groupRes.data.data as SubClientGroup & { sub_clients?: SubClient[] };
      setSelectedSubClientIds((g.sub_clients || []).map((s) => s.id));
      setAllSubClientsForGroup(subClientsRes.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load group');
    } finally {
      setLoadingGroupSubClients(false);
    }
  };

  const openCreateGroup = async () => {
    setEditingGroup(null);
    setGroupForm({ name: '', description: '' });
    setSelectedSubClientIds([]);
    setGroupDialogOpen(true);
    setLoadingGroupSubClients(true);
    try {
      const res = await subClientService.getAll();
      setAllSubClientsForGroup(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load clients');
    } finally {
      setLoadingGroupSubClients(false);
    }
  };

  const toggleSubClientInGroup = (id: number) => {
    setSelectedSubClientIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAllSubClientsInGroup = () => {
    if (selectedSubClientIds.length === allSubClientsForGroup.length) {
      setSelectedSubClientIds([]);
    } else {
      setSelectedSubClientIds(allSubClientsForGroup.map((s) => s.id));
    }
  };

  const checkDuplicateEmail = async () => {
    if (editingSubClient) return;
    const email = formData.email.trim();
    if (!email) {
      setDuplicateEmailClient(null);
      return;
    }
    try {
      const res = await subClientService.getAll({ search: email });
      const exact = res.data.data.find((c) => c.email?.toLowerCase() === email.toLowerCase());
      setDuplicateEmailClient(exact ?? null);
    } catch {
      setDuplicateEmailClient(null);
    }
  };

  const checkDuplicatePhone = async () => {
    if (editingSubClient) return;
    const digits = normalizePhoneDigits(formData.phone);
    if (!digits) {
      setDuplicatePhoneClient(null);
      return;
    }
    try {
      const res = await subClientService.getAll({ search: formData.phone.trim() });
      const exact = res.data.data.find(
        (c) => c.phone && normalizePhoneDigits(c.phone) === digits
      );
      setDuplicatePhoneClient(exact ?? null);
    } catch {
      setDuplicatePhoneClient(null);
    }
  };

  const handleOpenDialog = () => {
    resetForm();
    setEditingSubClient(null);
    setDuplicateEmailClient(null);
    setDuplicatePhoneClient(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSubClient(null);
    resetForm();
    setDuplicateEmailClient(null);
    setDuplicatePhoneClient(null);
  };

  const getTypeIcon = (type: string) => {
    return type === 'company' ? <BusinessIcon /> : <PersonIcon />;
  };

  const getTypeColor = (type: string) => {
    return type === 'company' ? 'primary' : 'secondary';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Mes Clients
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<BadgeIcon />}
            onClick={() => { setIdScannerTarget(null); setIdScannerOpen(true); }}
          >
            Scanner ID
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Ajouter un Client
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={currentTab} onChange={(_, v) => setCurrentTab(v)} sx={{ mb: 2 }}>
        <Tab label="Clients" id="mes-clients-tab-0" aria-controls="mes-clients-panel-0" />
        <Tab label="Groupes" id="mes-clients-tab-1" aria-controls="mes-clients-panel-1" />
      </Tabs>

      {/* Tab: Clients */}
      <Box role="tabpanel" hidden={currentTab !== 0} id="mes-clients-panel-0" aria-labelledby="mes-clients-tab-0">
      <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
        <InputLabel>Filtrer par groupe</InputLabel>
        <Select
          value={groupFilter === '' ? '' : String(groupFilter)}
          label="Filtrer par groupe"
          onChange={(e) => {
            const val = e.target.value;
            setGroupFilter(val === '' ? '' : Number(val));
          }}
        >
          <MenuItem value="">Tous les clients</MenuItem>
          {groups.map((g) => (
            <MenuItem key={g.id} value={String(g.id)}>{g.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Clients
                  </Typography>
                  <Typography variant="h4">
                    {subClients.length}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <BusinessIcon />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Entreprises
                  </Typography>
                  <Typography variant="h4">
                    {subClients.filter(c => c.type === 'company').length}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Particuliers
                  </Typography>
                  <Typography variant="h4">
                    {subClients.filter(c => c.type === 'individual').length}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <VisibilityIcon />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Actifs
                  </Typography>
                  <Typography variant="h4">
                    {subClients.filter(c => c.is_active).length}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sub-clients Table */}
      <Card sx={{ mt: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Groupe</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Adresse</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Aucun client trouvé
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Commencez par ajouter votre premier client
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                subClients.map((subClient) => (
                  <TableRow
                    key={subClient.id}
                    hover
                    id={`sub-client-row-${subClient.id}`}
                    sx={{
                      transition: 'box-shadow 0.2s ease',
                      ...(highlightedId === subClient.id
                        ? {
                            border: '2px solid',
                            borderColor: 'primary.main',
                            boxShadow: 4,
                          }
                        : {}),
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: getTypeColor(subClient.type) + '.main' }}>
                          {getTypeIcon(subClient.type)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {subClient.type === 'company' && subClient.company_name 
                              ? subClient.company_name 
                              : subClient.name}
                          </Typography>
                          {subClient.type === 'company' && subClient.company_name && (
                            <Typography variant="body2" color="text.secondary">
                              {subClient.name}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {subClient.group ? (
                        <Chip label={subClient.group.name} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={subClient.type === 'company' ? 'Entreprise' : 'Particulier'}
                        color={getTypeColor(subClient.type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="body2">{subClient.email}</Typography>
                        </Box>
                        {subClient.phone && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <PhoneIcon fontSize="small" color="action" />
                            <Typography variant="body2">{subClient.phone}</Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {subClient.address && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <LocationIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {subClient.city && subClient.postal_code 
                              ? `${subClient.city}, ${subClient.postal_code}`
                              : subClient.address}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={subClient.is_active ? 'Actif' : 'Inactif'}
                        color={subClient.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="Fiche client — modification, documents, réservations">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/sub-clients/${subClient.id}`)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Liste des réservations (nouvel onglet)">
                          <IconButton
                            size="small"
                            onClick={() =>
                              window.open(
                                `${window.location.origin}/reservations?sub_client_id=${subClient.id}`,
                                '_blank',
                                'noopener,noreferrer'
                              )
                            }
                            color="secondary"
                          >
                            <EventAvailableIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer le client">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(subClient.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      </Box>

      {/* Tab: Groupes */}
      <Box role="tabpanel" hidden={currentTab !== 1} id="mes-clients-panel-1" aria-labelledby="mes-clients-tab-1">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="body1" color="text.secondary">
            Créez des groupes et assignez les clients qui en font partie.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateGroup}>
            Ajouter un groupe
          </Button>
        </Box>
        <Card>
          {loadingGroups ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Groupe</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Clients</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <FolderIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography color="text.secondary">Aucun groupe. Cliquez sur « Ajouter un groupe ».</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    groups.map((g) => (
                      <TableRow key={g.id}>
                        <TableCell>
                          <Typography fontWeight="medium">{g.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {g.description || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{g.sub_clients_count ?? 0}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Modifier">
                            <IconButton size="small" onClick={() => openEditGroup(g)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Supprimer">
                            <IconButton size="small" color="error" onClick={() => handleDeleteGroup(g)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSubClient ? 'Modifier le Client' : 'Ajouter un Nouveau Client'}
        </DialogTitle>
        <DialogContent>
          {!editingSubClient && duplicateEmailClient && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      handleEdit(duplicateEmailClient);
                      setDuplicateEmailClient(null);
                      setDuplicatePhoneClient(null);
                    }}
                  >
                    Utiliser ce client
                  </Button>
                  <Button size="small" onClick={() => setDuplicateEmailClient(null)}>
                    Continuer quand même
                  </Button>
                </Stack>
              }
            >
              Un client avec cet email existe déjà: {subClientDisplayName(duplicateEmailClient)} (N°
              {duplicateEmailClient.id})
            </Alert>
          )}
          {!editingSubClient && duplicatePhoneClient && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      handleEdit(duplicatePhoneClient);
                      setDuplicateEmailClient(null);
                      setDuplicatePhoneClient(null);
                    }}
                  >
                    Utiliser ce client
                  </Button>
                  <Button size="small" onClick={() => setDuplicatePhoneClient(null)}>
                    Continuer quand même
                  </Button>
                </Stack>
              }
            >
              Un client avec ce numéro existe déjà: {subClientDisplayName(duplicatePhoneClient)} (N°
              {duplicatePhoneClient.id})
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Type de Client</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'individual' | 'company' })}
                  label="Type de Client"
                >
                  <MenuItem value="individual">Particulier</MenuItem>
                  <MenuItem value="company">Entreprise</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Nom"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Prénom"
                value={formData.prenom}
                onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                required
              />
            </Grid>
            {formData.type === 'company' && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Nom de l'Entreprise"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="SIRET"
                    value={formData.siret}
                    onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                  />
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  setDuplicateEmailClient(null);
                }}
                onBlur={checkDuplicateEmail}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Téléphone"
                value={formData.phone}
                onChange={(e) => {
                  setFormData({ ...formData, phone: e.target.value });
                  setDuplicatePhoneClient(null);
                }}
                onBlur={checkDuplicatePhone}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Adresse"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="Ville"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="Code Postal"
                value={formData.postal_code}
                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Pays</InputLabel>
                <Select
                  label="Pays"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                >
                  <MenuItem value="">—</MenuItem>
                  {paysList.map((p) => (
                    <MenuItem key={p.id} value={p.nom_fr_fr}>{p.nom_fr_fr}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Client actif"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingSubClient ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Group Add/Edit Dialog */}
      <Dialog open={groupDialogOpen} onClose={() => { setGroupDialogOpen(false); setEditingGroup(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGroup ? 'Modifier le groupe' : 'Nouveau groupe'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Nom du groupe"
              value={groupForm.name}
              onChange={(e) => setGroupForm((p) => ({ ...p, name: e.target.value }))}
              required
            />
            <TextField
              fullWidth
              label="Description (optionnel)"
              multiline
              rows={2}
              value={groupForm.description}
              onChange={(e) => setGroupForm((p) => ({ ...p, description: e.target.value }))}
            />
            <Typography variant="subtitle2" sx={{ pt: 1 }}>
              Clients dans ce groupe
            </Typography>
            {loadingGroupSubClients ? (
              <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
            ) : allSubClientsForGroup.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Aucun client. Ajoutez d'abord des clients dans l'onglet « Clients ».
              </Typography>
            ) : (
              <>
                <Button size="small" onClick={selectAllSubClientsInGroup}>
                  {selectedSubClientIds.length === allSubClientsForGroup.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                </Button>
                <List dense sx={{ maxHeight: 280, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  {allSubClientsForGroup.map((sc) => (
                    <ListItemButton
                      key={sc.id}
                      onClick={() => toggleSubClientInGroup(sc.id)}
                      dense
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selectedSubClientIds.includes(sc.id)}
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={sc.type === 'company' && sc.company_name ? sc.company_name : sc.name}
                        secondary={sc.email}
                      />
                    </ListItemButton>
                  ))}
                </List>
                <Typography variant="caption" color="text.secondary">
                  {selectedSubClientIds.length} client(s) sélectionné(s)
                </Typography>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setGroupDialogOpen(false); setEditingGroup(null); }}>Annuler</Button>
          <Button onClick={handleSaveGroup} variant="contained" disabled={!groupForm.name.trim() || savingGroup}>
            {savingGroup ? <CircularProgress size={20} /> : editingGroup ? 'Enregistrer' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ID Scanner Modal */}
      <IDScannerModal
        open={idScannerOpen}
        onClose={() => { setIdScannerOpen(false); setIdScannerTarget(null); }}
        existingClientId={idScannerTarget?.id}
        existingClientName={idScannerTarget ? (idScannerTarget.type === 'company' && idScannerTarget.company_name ? idScannerTarget.company_name : idScannerTarget.name) : undefined}
        onClientCreated={() => { loadSubClients(); setIdScannerOpen(false); setIdScannerTarget(null); }}
        onDocumentAttached={() => { loadSubClients(); setIdScannerOpen(false); setIdScannerTarget(null); }}
      />
    </Box>
  );
};

export default SubClients;
